import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Calendar, Play, Edit, Trash2, TrendingUp, TrendingDown, Clock, XCircle, Download } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  getTransactionsPeriodiques, 
  toggleTransactionPeriodique, 
  executeTransactionPeriodique, 
  deleteTransactionPeriodique,
  TransactionPeriodiqueWithDetails 
} from '../lib/services/PeriodicTransactionService';
import { generateTransactionsForMonth } from '../lib/services/GeneratePeriodicTransactionsService';
import { getTransactionAttachmentSignedUrl } from '../lib/services/transactionAttachmentService';
import { downloadPeriodicTransactionsCSV } from '../lib/services/ExportService';
import AddPeriodicTransactionForm from '../components/AddPeriodicTransactionForm';
import { getFrequencyLabel, DEFAULT_COLORS } from '../config/constants';

const getInitials = (text: string) => {
  const parts = (text || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};



// Liste des mois en français
const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// Fonction pour formater la date
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), 'dd MMMM yyyy', { locale: fr });
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return dateString;
  }
};

// Fonction pour obtenir le texte de fréquence
const getFrequenceText = (frequence: string) => {
  return getFrequencyLabel(frequence);
};

// Fonction pour obtenir la couleur en fonction du type
const getTypeColor = (type: string) => {
  switch (type) {
    case 'depense': return '#FF5252';
    case 'revenu': return DEFAULT_COLORS.CATEGORY;
    default: return '#888';
  }
};

// Fonction pour obtenir l'icône en fonction du type
const getTypeIcon = (type: string) => {
  switch (type) {
    case 'depense': return TrendingDown;
    case 'revenu': return TrendingUp;
    default: return Clock;
  }
};

const PeriodicTransactions: React.FC = () => {
  const [transactions, setTransactions] = useState<TransactionPeriodiqueWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionPeriodiqueWithDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // États pour la génération des transactions périodiques
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<string>(MONTHS[currentDate.getMonth()] || 'Janvier');
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [generatingTransactions, setGeneratingTransactions] = useState(false);
  const [generationResult, setGenerationResult] = useState<{ totalGenerated: number; alreadyExisting: number; errors: number; details: string[] } | null>(null);
  const [showGenerationResultModal, setShowGenerationResultModal] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [transactionToEdit, setTransactionToEdit] = useState<TransactionPeriodiqueWithDetails | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [iconUrlByPath, setIconUrlByPath] = useState<Record<string, string>>({});
  const [detailIconUrl, setDetailIconUrl] = useState<string | null>(null);

  // Charger les transactions périodiques
  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Chargement des transactions périodiques...');
      const { data, error } = await getTransactionsPeriodiques();
      
      if (error) {
        console.error('Erreur lors du chargement des transactions périodiques:', error);
        // Ne pas afficher d'alert pour éviter les popups gênants
        setTransactions([]);
      } else {
        console.log('Transactions périodiques chargées:', data);
        setTransactions(data || []);
      }
    } catch (error) {
      console.error('Exception lors du chargement des transactions périodiques:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // URLs signées pour les icônes de liste
  useEffect(() => {
    let cancelled = false;
    const paths = Array.from(
      new Set(
        transactions
          .map((t) => t.attachment_path)
          .filter((p): p is string => !!p),
      ),
    );
    if (paths.length === 0) {
      setIconUrlByPath({});
      return;
    }
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        paths.map(async (path) => {
          const { url } = await getTransactionAttachmentSignedUrl(path);
          if (url) next[path] = url;
        }),
      );
      if (!cancelled) setIconUrlByPath(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [transactions]);

  useEffect(() => {
    let cancelled = false;
    const path = selectedTransaction?.attachment_path;
    if (!showDetailModal || !path) {
      setDetailIconUrl(null);
      return;
    }
    (async () => {
      const { url } = await getTransactionAttachmentSignedUrl(path);
      if (!cancelled) setDetailIconUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [showDetailModal, selectedTransaction?.id, selectedTransaction?.attachment_path]);

  // Charger les transactions au montage
  useEffect(() => {
    // Vérifier si l'utilisateur est connecté avant de charger
    const checkAuthAndLoad = async () => {
      try {
        const { data: { session } } = await import('../lib/supabaseClient').then(m => m.supabase.auth.getSession());
        setIsAuthenticated(!!session);
        if (session) {
          loadTransactions();
        } else {
          console.log('Utilisateur non connecté');
          setLoading(false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de l\'authentification:', error);
        setIsAuthenticated(false);
        setLoading(false);
      }
    };
    
    checkAuthAndLoad();
  }, [loadTransactions]);

  // Fonction pour générer les transactions périodiques
  const handleGenerateTransactions = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir générer les transactions périodiques pour ${selectedMonth} ${selectedYear} ?`)) {
      await generateTransactions();
    }
  };

  // Fonction qui effectue la génération des transactions
  const generateTransactions = async () => {
    try {
      setGeneratingTransactions(true);
      
      // Obtenir l'index du mois sélectionné (0-11)
      const monthIndex = MONTHS.indexOf(selectedMonth);
      
      // Générer les transactions
      const result = await generateTransactionsForMonth(selectedYear, monthIndex);
      
      setGenerationResult(result);
      setShowGenerationResultModal(true);
      
      // Recharger la liste des transactions périodiques
      loadTransactions();
    } catch (error) {
      console.error('Erreur lors de la génération des transactions:', error);
      alert('Une erreur est survenue lors de la génération des transactions');
    } finally {
      setGeneratingTransactions(false);
    }
  };

  // Activer/désactiver une transaction périodique
  const handleToggleActive = async (id: string, actif: boolean) => {
    try {
      const { data, error } = await toggleTransactionPeriodique(id, !actif);
      if (error) {
        console.error('Erreur lors de la modification du statut:', error);
        alert('Une erreur est survenue lors de la modification du statut');
      } else if (data) {
        // Mettre à jour la liste locale
        setTransactions(prevTransactions =>
          prevTransactions.map(t =>
            t.id === id ? { ...t, actif: !actif } : t
          )
        );
      }
    } catch (error) {
      console.error('Exception lors de la modification du statut:', error);
      alert('Une erreur est survenue lors de la modification du statut');
    }
  };

  // Exécuter manuellement une transaction périodique
  const handleExecuteTransaction = async (id: string) => {
    try {
      const { success, error } = await executeTransactionPeriodique(id);
      if (error) {
        console.error('Erreur lors de l\'exécution de la transaction:', error);
        alert('Une erreur est survenue lors de l\'exécution de la transaction');
      } else if (success) {
        alert('La transaction a été exécutée avec succès');
        loadTransactions();
      }
    } catch (error) {
      console.error('Exception lors de l\'exécution de la transaction:', error);
      alert('Une erreur est survenue lors de l\'exécution de la transaction');
    }
  };

  // Supprimer une transaction périodique
  const handleDeleteTransaction = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction périodique ?')) {
      try {
        const { success, error } = await deleteTransactionPeriodique(id);
        if (error) {
          console.error('Erreur lors de la suppression:', error);
          alert('Une erreur est survenue lors de la suppression');
        } else if (success) {
          setTransactions(prevTransactions =>
            prevTransactions.filter(t => t.id !== id)
          );
          if (selectedTransaction?.id === id) {
            setShowDetailModal(false);
          }
        }
      } catch (error) {
        console.error('Exception lors de la suppression:', error);
        alert('Une erreur est survenue lors de la suppression');
      }
    }
  };

  // Afficher les détails d'une transaction
  const showTransactionDetails = (transaction: TransactionPeriodiqueWithDetails) => {
    setSelectedTransaction(transaction);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-white mt-4">Chargement des transactions périodiques...</p>
        </div>
      </div>
    );
  }

  // Si pas d'utilisateur connecté, afficher un message
  if (isAuthenticated === false) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-white text-lg mb-4">Veuillez vous connecter pour accéder aux transactions périodiques</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
          >
            Se connecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec bouton d'ajout */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Transactions Périodiques</h2>
          <p className="text-dark-400">
            Gérez vos transactions récurrentes automatiques
          </p>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Nouvelle transaction</span>
        </button>
      </div>

      {/* Sélecteur de mois et année + bouton de génération */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex space-x-2">
          {/* Sélecteur de mois */}
          <div className="relative">
            <button
              onClick={() => setShowMonthPicker(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white hover:bg-dark-700 transition-colors"
            >
              <span>{selectedMonth}</span>
              <span>▼</span>
            </button>
            
            {showMonthPicker && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-lg z-10">
                {MONTHS.map((month) => (
                  <button
                    key={month}
                    onClick={() => {
                      setSelectedMonth(month);
                      setShowMonthPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 transition-colors"
                  >
                    {month}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Sélecteur d'année */}
          <div className="relative">
            <button
              onClick={() => setShowYearPicker(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white hover:bg-dark-700 transition-colors"
            >
              <span>{selectedYear}</span>
              <span>▼</span>
            </button>
            
            {showYearPicker && (
              <div className="absolute top-full left-0 mt-1 w-32 bg-dark-800 border border-dark-700 rounded-lg shadow-lg z-10">
                {Array.from({ length: 5 }, (_, i) => selectedYear - 2 + i).map((year) => (
                  <button
                    key={year}
                    onClick={() => {
                      setSelectedYear(year);
                      setShowYearPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 transition-colors"
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Boutons génération / export */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => downloadPeriodicTransactionsCSV(transactions)}
            disabled={transactions.length === 0}
            title="Exporter la liste au format CSV"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dark-600 bg-dark-800 text-dark-200 hover:bg-dark-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>CSV</span>
          </button>
          <button
            onClick={handleGenerateTransactions}
            disabled={generatingTransactions}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white rounded-lg transition-colors"
          >
            {generatingTransactions ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Calendar className="w-4 h-4" />
            )}
            <span>{generatingTransactions ? 'Génération...' : 'Générer'}</span>
          </button>
        </div>
      </div>

      {/* Liste des transactions */}
      <div className="space-y-3">
        {transactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-dark-400" />
            </div>
            <p className="text-white font-medium mb-2">
              Aucune transaction périodique
            </p>
            <p className="text-dark-400">
              Ajoutez votre première transaction périodique pour commencer
            </p>
          </div>
        ) : (
          transactions.map((transaction) => {
            const typeColor = getTypeColor(transaction.type);
            const TypeIcon = getTypeIcon(transaction.type);
            const isActive = transaction.actif;
            const iconUrl = transaction.attachment_path
              ? iconUrlByPath[transaction.attachment_path]
              : undefined;
            const accent = transaction.categorie?.couleur || typeColor;

            return (
              <div
                key={transaction.id}
                className={`flex items-center justify-between p-4 bg-dark-800 rounded-lg hover:bg-dark-700 transition-colors cursor-pointer ${!isActive ? 'opacity-60' : ''}`}
                onClick={() => showTransactionDetails(transaction)}
              >
                <div className="flex items-center space-x-4">
                  {/* Icône custom ou initiale / type */}
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border border-dark-600 flex-shrink-0"
                    style={{ backgroundColor: iconUrl ? '#111' : `${accent}33` }}
                  >
                    {iconUrl ? (
                      <img src={iconUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold" style={{ color: accent }}>
                        {getInitials(transaction.description)}
                      </span>
                    )}
                  </div>

                  {/* Informations de la transaction */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{transaction.description}</h3>
                    <div className="flex items-center space-x-4 mt-1 flex-wrap">
                      <span className="text-dark-400 text-sm">
                        {getFrequenceText(transaction.frequence)}
                      </span>
                      {transaction.prochaine_execution && (
                        <span className="text-dark-400 text-sm">
                          Prochaine: {formatDate(transaction.prochaine_execution)}
                        </span>
                      )}
                    </div>
                    <p className="text-dark-400 text-sm mt-0.5 truncate">
                      {transaction.type === 'transfert' && transaction.compte_destination?.nom
                        ? `${transaction.compte?.nom || 'Compte inconnu'} → ${transaction.compte_destination.nom}`
                        : transaction.compte?.nom || 'Compte inconnu'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <TypeIcon className="w-4 h-4 text-dark-400 hidden sm:block" />
                  {/* Montant */}
                  <span className="text-lg font-bold" style={{ color: typeColor }}>
                    {transaction.type === 'depense' ? '-' : ''}{transaction.montant.toFixed(2)} €
                  </span>

                  {/* Switch actif/inactif */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleActive(transaction.id!, isActive);
                    }}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      isActive ? 'bg-green-500' : 'bg-dark-600'
                    }`}
                  >
                    <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                      isActive ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de détails */}
      {selectedTransaction && showDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-dark-800 border border-dark-700 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Détails de la transaction</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-dark-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Description</label>
                <p className="text-white">{selectedTransaction.description}</p>
              </div>

              {!!selectedTransaction.attachment_path && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">Icône</label>
                  {detailIconUrl ? (
                    <a
                      href={detailIconUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-16 h-16 rounded-full overflow-hidden border border-dark-600 bg-dark-900"
                    >
                      <img src={detailIconUrl} alt="" className="w-full h-full object-cover" />
                    </a>
                  ) : (
                    <p className="text-dark-400 text-sm">Chargement…</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Montant</label>
                <p className="text-lg font-bold" style={{ color: getTypeColor(selectedTransaction.type) }}>
                  {selectedTransaction.type === 'depense' ? '-' : ''}{selectedTransaction.montant.toFixed(2)} €
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Type</label>
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: getTypeColor(selectedTransaction.type) }}
                  >
                    {React.createElement(getTypeIcon(selectedTransaction.type), { className: "w-3 h-3 text-white" })}
                  </div>
                  <span className="text-white capitalize">{selectedTransaction.type}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Catégorie</label>
                <p className="text-white">{selectedTransaction.categorie?.nom || 'Non catégorisé'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Compte</label>
                <p className="text-white">{selectedTransaction.compte?.nom || 'Compte inconnu'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Fréquence</label>
                <p className="text-white">{getFrequenceText(selectedTransaction.frequence)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Date de début</label>
                <p className="text-white">{formatDate(selectedTransaction.date_debut)}</p>
              </div>

              {selectedTransaction.date_fin && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">Date de fin</label>
                  <p className="text-white">{formatDate(selectedTransaction.date_fin)}</p>
                </div>
              )}

              {selectedTransaction.prochaine_execution && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">Prochaine exécution</label>
                  <p className="text-white">{formatDate(selectedTransaction.prochaine_execution)}</p>
                </div>
              )}

              {selectedTransaction.derniere_execution && (
                <div>
                  <label className="block text-sm font-medium text-dark-400 mb-1">Dernière exécution</label>
                  <p className="text-white">{formatDate(selectedTransaction.derniere_execution)}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Nombre d'exécutions</label>
                <p className="text-white">
                  {selectedTransaction.nombre_executions || 0}
                  {selectedTransaction.nombre_executions_max ? ` / ${selectedTransaction.nombre_executions_max}` : ''}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-dark-400 mb-1">Statut</label>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${selectedTransaction.actif ? 'bg-green-500' : 'bg-dark-600'}`} />
                  <span className="text-white">{selectedTransaction.actif ? 'Actif' : 'Inactif'}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-between space-x-3 pt-6">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleExecuteTransaction(selectedTransaction.id!);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
              >
                <Play className="w-4 h-4" />
                <span>Exécuter</span>
              </button>

              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setTransactionToEdit(selectedTransaction);
                  setShowAddForm(true);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Modifier</span>
              </button>

              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleDeleteTransaction(selectedTransaction.id!);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire d'ajout/édition de transaction périodique */}
      <AddPeriodicTransactionForm
        isOpen={showAddForm}
        onClose={() => {
          setShowAddForm(false);
          setTransactionToEdit(null);
        }}
        onSuccess={() => {
          loadTransactions();
          setTransactionToEdit(null);
        }}
        transactionToEdit={transactionToEdit}
      />

      {/* Modal de résultat de génération */}
      {generationResult && showGenerationResultModal && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 border border-dark-700 rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between shrink-0 px-6 pt-6 pb-4">
              <h2 className="text-xl font-bold text-white">Résultat de la génération</h2>
              <button
                type="button"
                onClick={() => setShowGenerationResultModal(false)}
                className="text-dark-400 hover:text-white transition-colors"
                aria-label="Fermer"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 space-y-4">
              <div className="flex justify-between">
                <span className="text-dark-400">Transactions générées :</span>
                <span className="text-green-400 font-bold">{generationResult.totalGenerated}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Transactions ignorées :</span>
                <span className="text-white">{generationResult.alreadyExisting}</span>
              </div>
              {generationResult.errors > 0 && (
                <div className="flex justify-between">
                  <span className="text-dark-400">Erreurs :</span>
                  <span className="text-red-400 font-bold">{generationResult.errors}</span>
                </div>
              )}

              {generationResult.details.length > 0 && (
                <div className="mt-2 pb-2">
                  <h3 className="text-white font-medium mb-2">Détails :</h3>
                  <div className="space-y-1">
                    {generationResult.details.map((detail, index) => (
                      <p key={index} className="text-sm text-dark-400">
                        • {detail}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="shrink-0 flex justify-end px-6 pt-4 pb-6 border-t border-dark-700">
              <button
                type="button"
                onClick={() => setShowGenerationResultModal(false)}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodicTransactions; 